/* sn-engraved-tag.js — the one place that draws a Supernormal engraved tag face.
 *
 * WHY THIS EXISTS
 * The engraved face was previously simulated twice, differently, on the same page:
 * the hero used a CSS circle with a single <span>, and engraved-finder.js stamped
 * one line of <text> onto tag-green.png. Both were wrong in the same way — they
 * drew the UNENGRAVED front, the one with the SUPERNORMAL wordmark across the
 * middle, and then squeezed the engraving in underneath it. The real engraved
 * variant has no wordmark on that face: the engraving IS the face, centred.
 *
 * Drawn rather than photographed because the text is dynamic, and the product
 * shot has "Molly" baked into it. Colours are sampled from that shot so the
 * drawn face and the photographed QR back sit together without clashing:
 *   disc #0C2524 -> #12332F, rim #193331, engraving pure white.
 *
 * THE ENGRAVING IS ALWAYS VERTICALLY CENTRED. One line or four, the block sits
 * in the middle of the disc with equal space above and below — that is how the
 * engraver lays it out, so that is what the preview has to show.
 *
 * Exposes window.SNEngravedTag:
 *   .svg(lines, opts)      -> SVG markup for the engraved face
 *   .fit(container)        -> clamp any line that overflows its disc chord
 *   .render(el, lines, o)  -> svg() into el, then fit() — use this in the browser
 *   .lines()               -> current non-empty lines from the page's engraving form
 *   'engraving:change' fires on document whenever those lines change.
 *
 * Also wires the buy form on /pages/engraved-dog-tags: per-line counters, the
 * empty-submit guard, and ordering several tags (one engraving each). Checkout
 * empties the cart first so nothing from the tag product page - its free tag +
 * Care trial pair - rides along; the engraved tag is bought on its own.
 */
(function () {
  'use strict';
  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false; // guard: sn-engraved-tag.js is also run head-less by _render-engraved-tag-svg.mjs
  var T = function (en, de) { return SN_DE ? de : en; };

  var MAX_LINES = 4;

  /* Geometry, in the 280x280 viewBox. The disc is a circle, so the width a line
     can use depends on how far it sits from the centre — a 4-line block's outer
     lines have measurably less room than its inner ones. */
  var CX = 140, CY = 150, R = 110;
  var PAD = 15;                    // keep engraving off the rim
  var R_TEXT = R - PAD;
  var LINE_RATIO = 1.16;           // line height as a multiple of font size
  var ADVANCE = 0.63;              // avg glyph advance for bold caps, in em (measured, not guessed)
  var CAP = 0.36;                  // half cap-height, in em
  var SIZE_CAP = [0, 40, 33, 26, 21];  // largest size we allow, by line count
  var MIN_SIZE = 10;

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* Half-width available at a given vertical offset from the disc centre. */
  function halfChord(dy) {
    var inner = R_TEXT * R_TEXT - dy * dy;
    return inner > 0 ? Math.sqrt(inner) : 0;
  }

  /* Largest font size at which every line fits inside the disc. */
  function fitSize(lines) {
    var n = lines.length;
    var size = SIZE_CAP[Math.min(n, MAX_LINES)] || SIZE_CAP[MAX_LINES];
    for (; size > MIN_SIZE; size -= 0.5) {
      var lineHeight = size * LINE_RATIO;
      var ok = true;
      for (var i = 0; i < n; i++) {
        var offset = (i - (n - 1) / 2) * lineHeight;
        // Measure at the outer edge of the glyphs, not the baseline.
        var dy = Math.abs(offset) + size * CAP;
        if (lines[i].length * size * ADVANCE > halfChord(dy) * 2) { ok = false; break; }
      }
      if (ok) return size;
    }
    return MIN_SIZE;
  }

  function normalise(lines) {
    return (lines || [])
      .slice(0, MAX_LINES)
      .map(function (l) { return String(l == null ? '' : l).trim().toUpperCase(); })
      .filter(Boolean);
  }

  /**
   * @param {string[]} lines  up to 4 lines of engraving
   * @param {object}  [opts]  { id: unique suffix for gradient ids, label: aria-label }
   */
  function svg(lines, opts) {
    opts = opts || {};
    var uid = opts.id || 'sn-eng';
    var text = normalise(lines);
    var size = text.length ? fitSize(text) : 0;
    var lineHeight = size * LINE_RATIO;

    var body = '';
    for (var i = 0; i < text.length; i++) {
      var offset = (i - (text.length - 1) / 2) * lineHeight;
      var y = CY + offset;
      // How much room this line actually has, given how far it sits from the centre.
      // Carried on the element so fit() can clamp against the REAL rendered width —
      // the ADVANCE estimate above sizes the block, but a line of Ms and Ws is much
      // wider than the average it assumes, and would spill over the rim unnoticed.
      var avail = halfChord(Math.abs(offset) + size * CAP) * 2;
      body += '<text x="' + CX + '" y="' + y.toFixed(1) + '" text-anchor="middle" dominant-baseline="central"' +
        ' fill="#FFFFFF" font-family="Helvetica Neue, Helvetica, Arial, sans-serif" font-weight="700"' +
        ' font-size="' + size.toFixed(1) + '" data-max="' + avail.toFixed(1) + '">' + esc(text[i]) + '</text>';
    }

    return '<svg viewBox="0 0 280 280" width="100%" height="100%" role="img" aria-label="' +
      esc(opts.label || (text.length ? T('Front of the tag, engraved with ', 'Vorderseite der Marke, graviert mit ') + text.join(', ') : T('Front of the tag, not yet engraved', 'Vorderseite der Marke, noch nicht graviert'))) + '">' +
      '<defs>' +
        '<linearGradient id="' + uid + '-face" x1="0" y1="0" x2="0" y2="1">' +
          '<stop offset="0" stop-color="#0C2524"></stop>' +
          '<stop offset="1" stop-color="#12332F"></stop>' +
        '</linearGradient>' +
        '<radialGradient id="' + uid + '-sheen" cx="0.32" cy="0.22" r="0.85">' +
          '<stop offset="0" stop-color="#FFFFFF" stop-opacity="0.14"></stop>' +
          '<stop offset="0.55" stop-color="#FFFFFF" stop-opacity="0"></stop>' +
        '</radialGradient>' +
      '</defs>' +
      // split ring through the hole
      '<path d="M140 30 m-15 0 a15 15 0 1 0 30 0 a15 15 0 1 0 -30 0" fill="none" stroke="#8A8F92" stroke-width="4"></path>' +
      '<circle cx="' + CX + '" cy="' + CY + '" r="' + R + '" fill="url(#' + uid + '-face)"></circle>' +
      '<circle cx="' + CX + '" cy="' + CY + '" r="' + (R - 1) + '" fill="none" stroke="#193331" stroke-width="2"></circle>' +
      '<circle cx="' + CX + '" cy="' + CY + '" r="' + R + '" fill="url(#' + uid + '-sheen)"></circle>' +
      '<circle cx="' + CX + '" cy="' + (CY - R + 18) + '" r="9" fill="#081A19"></circle>' +
      body +
      '</svg>';
  }

  /* Condense any line whose real rendered width overflows its chord. Only ever
     shrinks — a line that fits keeps its natural letter spacing. */
  function fit(container) {
    if (!container || !container.querySelectorAll) return;
    var nodes = container.querySelectorAll('text[data-max]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var max = parseFloat(node.getAttribute('data-max'));
      if (!max || typeof node.getComputedTextLength !== 'function') continue;
      var width;
      try { width = node.getComputedTextLength(); } catch (e) { continue; }
      if (width > max) {
        node.setAttribute('textLength', max.toFixed(1));
        node.setAttribute('lengthAdjust', 'spacingAndGlyphs');
      }
    }
  }

  function render(el, lines, opts) {
    if (!el) return;
    el.innerHTML = svg(lines, opts);
    fit(el);
  }

  // ---- page wiring ---------------------------------------------------------
  var root = document.getElementById('sn-engraved-dog-tags');
  var inputs = root ? Array.prototype.slice.call(root.querySelectorAll('[data-engrave-line]')) : [];

  function currentLines() {
    return inputs.map(function (i) { return i.value.trim(); }).filter(Boolean);
  }

  window.SNEngravedTag = { svg: svg, fit: fit, render: render, lines: currentLines, MAX_LINES: MAX_LINES };

  if (!root || !inputs.length) return;

  var preview = root.querySelector('[data-engrave-preview]');
  var valueInput = root.querySelector('[data-engrave-value]');
  var form = root.querySelector('[data-engrave-form]');
  var perLineMax = parseInt(inputs[0].getAttribute('maxlength'), 10) || 15;
  // What the empty preview shows, faded: data-engrave-sample="LINE 1|LINE 2" on the preview, else YOUR TEXT.
  var sample = preview && preview.getAttribute('data-engrave-sample');
  var sampleLines = sample ? sample.split('|') : ['YOUR TEXT'];

  /* ---- several tags ---------------------------------------------------------
     The four inputs edit ONE tag at a time - the active one. Each tag keeps its
     own lines here; "same on every tag" collapses them all onto tag 1. On submit
     each distinct engraving becomes its own cart line (identical ones share a
     line), because the engraving is a line-item property and a line carries one. */
  // A page can lower the cap with data-max-tags on the form (the £5 engraved product page sells up to 4).
  var MAX_TAGS = (form && parseInt(form.getAttribute('data-max-tags'), 10)) || 10;
  var qtyWrap = form && form.querySelector('[data-engrave-qty-wrap]');
  var countOut = form && form.querySelector('[data-engrave-count]');
  var minus = form && form.querySelector('[data-engrave-qty="-"]');
  var plus = form && form.querySelector('[data-engrave-qty="+"]');
  var tabsWrap = form && form.querySelector('[data-engrave-tabs]');
  var tablist = form && form.querySelector('[data-engrave-tablist]');
  var sameBox = form && form.querySelector('[data-engrave-same]');
  var totalEl = form && form.querySelector('[data-engrave-total]');
  var eachEl = form && form.querySelector('[data-engrave-each]');
  var errorEl = form && form.querySelector('[data-engrave-error]');
  var button = form && form.querySelector('button[type="submit"]');
  var buttonLabelEl = button && button.querySelector('[data-engrave-button-label]');
  var buttonDefault = buttonLabelEl ? buttonLabelEl.textContent.trim() : '';
  var unitPrice = totalEl ? parseFloat(totalEl.getAttribute('data-unit')) || 9 : 9;

  var tags = [inputs.map(function (i) { return i.value; })];
  var active = 0;
  var same = false;

  function blank() { return inputs.map(function () { return ''; }); }
  function linesOf(t) { return t.map(function (l) { return l.trim(); }).filter(Boolean); }
  function editing() { return same ? 0 : active; }
  function formatGbp(v) { return v % 1 ? v.toFixed(2) : String(v); }

  function setButton(label) { if (buttonLabelEl) buttonLabelEl.textContent = label; }

  function buttonLabel() {
    var n = tags.length;
    if (n < 2) return buttonDefault;
    return T('Engrave ' + n + ' tags - checkout', n + ' Marken gravieren & zur Kasse');
  }

  function clearError() {
    if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
    inputs[0].style.borderColor = '';
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  function renderTabs() {
    var n = tags.length;
    if (countOut) countOut.textContent = String(n);
    if (minus) minus.disabled = n <= 1;
    if (plus) plus.disabled = n >= MAX_TAGS;
    if (tabsWrap) tabsWrap.hidden = n < 2;
    if (tablist) {
      tablist.hidden = same;
      tablist.innerHTML = '';
      tags.forEach(function (t, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sn-eng-tab';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', i === active ? 'true' : 'false');
        var first = linesOf(t)[0];
        b.textContent = T('Tag ', 'Marke ') + (i + 1) + (first ? ' · ' + first.toUpperCase() : '');
        if (!first) {
          // A red dot marks a tag that still needs its engraving.
          var dot = document.createElement('span');
          dot.className = 'sn-eng-tab__dot';
          dot.setAttribute('aria-label', T('(not filled in yet)', '(noch leer)'));
          b.appendChild(dot);
        }
        b.addEventListener('click', function () { show(i); });
        tablist.appendChild(b);
      });
    }
    // Plain GBP text: sn-money.js's MutationObserver localises it like the rest of the page.
    if (totalEl) totalEl.textContent = '£' + formatGbp(unitPrice * n);
    if (eachEl) eachEl.hidden = n < 2;
    if (!busy) setButton(buttonLabel());
  }

  // Load a tag's lines into the four inputs.
  function show(i) {
    active = i;
    var t = tags[editing()];
    inputs.forEach(function (input, k) { input.value = t[k] || ''; });
    clearError();
    sync();
  }

  function sync() {
    var lines = currentLines();
    tags[editing()] = inputs.map(function (i) { return i.value; });

    // The hidden field only matters without JS (the form then posts tag 1 on its
    // own). Lines are joined with " / " so the engraving stays readable on the
    // order screen - the JS path below joins them the same way.
    if (valueInput) valueInput.value = linesOf(tags[0]).join(' / ');

    if (preview) {
      render(preview, lines.length ? lines : sampleLines, {
        id: 'sn-hero',
        label: lines.length ? T('Front of the tag, engraved with ', 'Vorderseite der Marke, graviert mit ') + lines.join(', ') : T('Front of the tag, not yet engraved', 'Vorderseite der Marke, noch nicht graviert'),
      });
      preview.style.opacity = lines.length ? '1' : '0.5';
    }

    inputs.forEach(function (input) {
      var n = input.getAttribute('data-engrave-line');
      var counter = root.querySelector('[data-engrave-counter="' + n + '"]');
      if (counter) counter.textContent = input.value.length + '/' + perLineMax;
    });

    renderTabs();
    document.dispatchEvent(new CustomEvent('engraving:change', { detail: { lines: lines } }));
  }

  inputs.forEach(function (input) {
    input.addEventListener('input', function () { clearError(); sync(); });
  });

  if (qtyWrap) qtyWrap.hidden = false;   // the stepper needs JS, so it ships hidden

  if (plus) plus.addEventListener('click', function () {
    if (tags.length >= MAX_TAGS) return;
    tags.push(same ? tags[0].slice() : blank());
    show(same ? 0 : tags.length - 1);
  });

  if (minus) minus.addEventListener('click', function () {
    if (tags.length <= 1) return;
    tags.pop();
    show(Math.min(active, tags.length - 1));
  });

  if (sameBox) sameBox.addEventListener('change', function () {
    same = sameBox.checked;
    // Leaving "same" mode: any tag still blank starts from tag 1's text rather
    // than empty, so nobody has to retype a shared phone number.
    if (!same) {
      tags.forEach(function (t, i) { if (i && !linesOf(t).length) tags[i] = tags[0].slice(); });
    }
    show(same ? 0 : active);
  });

  function cartItems() {
    var variant = Number(form.querySelector('input[name="id"]').value);
    var list = same ? tags.map(function () { return tags[0]; }) : tags;
    var count = {};
    var order = [];
    list.forEach(function (t) {
      var text = linesOf(t).join(' / ');
      if (!count[text]) { count[text] = 0; order.push(text); }
      count[text]++;
    });
    return order.map(function (text) {
      return { id: variant, quantity: count[text], properties: { Engraving: text } };
    });
  }

  var busy = false;
  function unlock() {
    busy = false;
    if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    setButton(buttonLabel());
  }
  // Back from checkout restores the page from bfcache with the button still locked.
  window.addEventListener('pageshow', function (e) { if (e.persisted) unlock(); });

  if (form) {
    form.addEventListener('submit', function (e) {
      // Don't let an empty engraving reach checkout - that's an unengravable order.
      var empty = -1;
      (same ? [tags[0]] : tags).some(function (t, i) {
        if (!linesOf(t).length) { empty = i; return true; }
        return false;
      });
      if (empty >= 0) {
        e.preventDefault();
        show(empty);
        inputs[0].focus();
        inputs[0].style.borderColor = '#B4462F';
        if (tags.length > 1 && !same) showError(T('Tag ' + (empty + 1) + ' needs at least one line.', 'Marke ' + (empty + 1) + ' braucht mindestens eine Zeile.'));
        return;
      }

      // Without fetch, fall through to the plain form post (one tag).
      if (!window.fetch) return;
      e.preventDefault();
      if (busy) return;
      busy = true;
      clearError();
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      setButton(T('Going to checkout…', 'Weiter zur Kasse…'));

      /* Empty the cart first. Anything already in it - typically the free tag +
         Care trial pair from the tag product page - would otherwise ride along
         into this checkout, adding a subscription the buyer never chose here. */
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var json = { 'Content-Type': 'application/json', Accept: 'application/json' };
      fetch(base + 'cart/clear.js', { method: 'POST', headers: json })
        .then(function (r) { if (!r.ok) throw new Error('clear ' + r.status); })
        .then(function () {
          return fetch(base + 'cart/add.js', { method: 'POST', headers: json, body: JSON.stringify({ items: cartItems() }) });
        })
        .then(function (r) {
          if (!r.ok) throw new Error('add ' + r.status);
          window.location.href = base + 'checkout';
        })
        .catch(function () {
          unlock();
          showError(T('Sorry, that didn’t go through. Please try again.', 'Das hat leider nicht geklappt. Bitte versuchen Sie es noch einmal.'));
        });
    });
  }

  sync();

  // The closing CTA points at the first line; focus it rather than just jumping.
  // Any such link on the page (the product page has CTAs in other sections, outside root).
  Array.prototype.forEach.call(document.querySelectorAll('a[href="#sn-engrave-line-1"]'), function (jump) {
    jump.addEventListener('click', function (e) {
      e.preventDefault();
      inputs[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(function () { inputs[0].focus(); inputs[0].select(); }, 420);
    });
  });
})();
